'use client';

import { c as _c } from "react/compiler-runtime";
import { useMediaState } from '@7f-ui/hooks/use-media-state';
import { useAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useCallback, useState, useTransition } from 'react';
import { useAppExperienceAvailableDates } from '../_hooks/use-app-experience-available-dates';
import { useAppExperienceTimeslotsPreview } from '../_hooks/use-app-experience-timeslots-preview';
import { useCurrentPathSearch } from '../_hooks/use-current-path-search';
import { useSelectedEdpTimeslot } from '../_hooks/use-selected-edp-timeslot';
import { headlinePrice } from '../_lib/day-prices';
import { edpBookingModalOpenAtom } from '../_lib/edp-booking-atoms';
import { buildTermineHref, FilterType, getExperienceSearchParams } from '../_lib/experience-booking-utils';
import type { ExperienceFilter } from '../_lib/experience-state-types';
import type { BookingQueryError } from './app-booking-query-error';
import { AppDateModal } from './app-date-modal';
import { AppEdpBookingModal } from './app-edp-booking-modal';
import { AppExperienceBookingBoxDesktop } from './app-experience-booking-box-desktop';
import { AppExperienceBookingBoxMobile } from './app-experience-booking-box-mobile';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceTicketBookingBox } from './app-experience-ticket-booking-box';
import { AppMultiVariantModal } from './app-multi-variant-modal';
import { AppPersonModal } from './app-person-modal';
import { AppVariantModal } from './app-variant-modal';
import { useEdpDateSelectionArm } from './edp-date-selection-arm';
type AppExperienceBookingBoxProps = {
  price: {
    price: number;
    category: string;
  } | null;
  location: string;
  onOpenReviews: () => void;
};
export function AppExperienceBookingBox(t0) {
  const $ = _c(12);
  const {
    price,
    location,
    onOpenReviews
  } = t0;
  const {
    experience,
    dayPrice,
    dayPriceFailed
  } = useExperienceFilterContext();
  let t1;
  if ($[0] !== dayPrice || $[1] !== dayPriceFailed || $[2] !== price) {
    t1 = dayPriceFailed ? null : headlinePrice(price, dayPrice);
    $[0] = dayPrice;
    $[1] = dayPriceFailed;
    $[2] = price;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const pricedForDay = t1;
  if (experience.experienceType === "ticket") {
    let t2;
    if ($[4] !== location || $[5] !== onOpenReviews || $[6] !== pricedForDay) {
      t2 = <AppExperienceTicketBookingBox price={pricedForDay} location={location} onOpenReviews={onOpenReviews} />;
      $[4] = location;
      $[5] = onOpenReviews;
      $[6] = pricedForDay;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t2;
  if ($[8] !== location || $[9] !== onOpenReviews || $[10] !== pricedForDay) {
    t2 = <AppExperienceTimeslotBookingBox price={pricedForDay} location={location} onOpenReviews={onOpenReviews} />;
    $[8] = location;
    $[9] = onOpenReviews;
    $[10] = pricedForDay;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function AppExperienceTimeslotBookingBox(t0) {
  const $ = _c(80);
  const {
    price,
    location,
    onOpenReviews
  } = t0;
  const router = useRouter();
  const {
    basePath
  } = useCurrentPathSearch();
  const isMobile = useMediaState("mobile");
  const {
    isVariant
  } = useEdpDateSelectionArm();
  const [activeModal, setActiveModal] = useState(null);
  const [isEdpBookingOpen, setIsEdpBookingOpen] = useAtom(edpBookingModalOpenAtom);
  const [isNavigatingToChoices, startNavigateToChoices] = useTransition();
  const {
    filter,
    experience,
    dayPriceFailed,
    dayPriceIsFetching,
    dayPriceIsPreliminary,
    refetchDayPrices
  } = useExperienceFilterContext();
  const preview = useAppExperienceTimeslotsPreview();
  const selected = useSelectedEdpTimeslot(preview);
  const {
    dates,
    prices: previewPrices,
    isLoading: isPreviewLoading,
    isError: previewFailed,
    isFetching: isPreviewFetching,
    isPlaceholderData: previewIsPreliminary,
    refetch: refetchPreview
  } = preview;
  const {
    availableDates,
    isLoading,
    isPreliminary,
    isError: availableDatesFailed,
    isFetching: isAvailableDatesFetching,
    refetch: refetchAvailableDates
  } = useAppExperienceAvailableDates(experience.publicId, filter.selectedVariant, filter.selectedOptions);
  let t1;
  if ($[0] !== basePath || $[1] !== isMobile || $[2] !== isVariant || $[3] !== router || $[4] !== setIsEdpBookingOpen) {
    t1 = async updatedFilter => {
      if (isVariant) {
        setActiveModal(null);
        setIsEdpBookingOpen(true);
        return;
      }
      if (isMobile) {
        const params = getExperienceSearchParams(updatedFilter);
        const termineHref = buildTermineHref(basePath, params);
        startNavigateToChoices(() => {
          router.push(termineHref);
        });
        return;
      }
      setActiveModal(null);
    };
    $[0] = basePath;
    $[1] = isMobile;
    $[2] = isVariant;
    $[3] = router;
    $[4] = setIsEdpBookingOpen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handlePaxSubmit = t1;
  const availabilityFailed = previewFailed || availableDatesFailed || selected.availabilityFailed;
  const priceFailed = dayPriceFailed || previewPrices.isError || selected.priceFailed;
  const isRetrying = dayPriceIsFetching || previewPrices.isFetching || isPreviewFetching || isAvailableDatesFetching || selected.isFetching;
  const hasQueryError = priceFailed || availabilityFailed;
  let t2;
  if ($[6] !== availabilityFailed || $[7] !== availableDatesFailed || $[8] !== dayPriceFailed || $[9] !== hasQueryError || $[10] !== isRetrying || $[11] !== previewFailed || $[12] !== previewPrices || $[13] !== priceFailed || $[14] !== refetchAvailableDates || $[15] !== refetchDayPrices || $[16] !== refetchPreview || $[17] !== selected) {
    t2 = hasQueryError ? {
      priceFailed,
      availabilityFailed,
      isRetrying,
      onRetry: () => {
        if (isRetrying) {
          return;
        }
        if (dayPriceFailed) {
          refetchDayPrices({
            cancelRefetch: false
          });
        }
        if (previewPrices.isError) {
          previewPrices.refetch({
            cancelRefetch: false
          });
        }
        if (previewFailed) {
          refetchPreview({
            cancelRefetch: false
          });
        }
        if (availableDatesFailed) {
          refetchAvailableDates({
            cancelRefetch: false
          });
        }
        if (selected.availabilityFailed) {
          selected.refetchAvailability({
            cancelRefetch: false
          });
        }
        if (selected.priceFailed) {
          selected.refetchPrices({
            cancelRefetch: false
          });
        }
      }
    } : undefined;
    $[6] = availabilityFailed;
    $[7] = availableDatesFailed;
    $[8] = dayPriceFailed;
    $[9] = hasQueryError;
    $[10] = isRetrying;
    $[11] = previewFailed;
    $[12] = previewPrices;
    $[13] = priceFailed;
    $[14] = refetchAvailableDates;
    $[15] = refetchDayPrices;
    $[16] = refetchPreview;
    $[17] = selected;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  const bookingError = t2;
  const bookingPending = isLoading || isPreviewLoading || dayPriceIsPreliminary || previewPrices.arePreliminary || isPreliminary || previewIsPreliminary || selected.isPending;
  const hasTimeslots = bookingPending || hasQueryError || !!availableDates?.length;
  const hasMultipleVariants = experience.variants?.length > 1;
  const t3 = hasQueryError || bookingPending ? null : price;
  const t4 = hasQueryError || bookingPending ? null : selected.timeslot;
  const t5 = !!dates?.length;
  let t6;
  let t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setActiveModal(FilterType.variant);
    t7 = () => setActiveModal(FilterType.person);
    t8 = () => setActiveModal(FilterType.date);
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
  }
  let t9;
  if ($[22] !== hasMultipleVariants || $[23] !== isVariant) {
    t9 = isVariant ? () => setActiveModal(hasMultipleVariants ? FilterType.variant : FilterType.person) : undefined;
    $[22] = hasMultipleVariants;
    $[23] = isVariant;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== bookingError || $[26] !== bookingPending || $[27] !== experience.userTimeslotRequestsAllowed || $[28] !== hasMultipleVariants || $[29] !== hasTimeslots || $[30] !== location || $[31] !== onOpenReviews || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t9) {
    t10 = <div className="hidden sm:contents"><AppExperienceBookingBoxDesktop price={t3} selectedTimeslot={t4} bookingError={bookingError} bookingPending={bookingPending} location={location} onOpenReviews={onOpenReviews} hasTimeslots={hasTimeslots} hasMultipleVariants={hasMultipleVariants} hasAvailableDates={t5} userTimeslotRequestsAllowed={experience.userTimeslotRequestsAllowed} onVariantClick={t6} onPaxClick={t7} onDateClick={t8} onCtaClick={t9} /></div>;
    $[25] = bookingError;
    $[26] = bookingPending;
    $[27] = experience.userTimeslotRequestsAllowed;
    $[28] = hasMultipleVariants;
    $[29] = hasTimeslots;
    $[30] = location;
    $[31] = onOpenReviews;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const t11 = hasQueryError || bookingPending ? null : price;
  const t12 = hasQueryError || bookingPending ? null : selected.timeslot;
  let t13;
  if ($[37] !== hasMultipleVariants) {
    t13 = () => setActiveModal(hasMultipleVariants ? FilterType.variant : FilterType.person);
    $[37] = hasMultipleVariants;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setActiveModal(FilterType.date);
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = !!dates?.length;
  let t16;
  if ($[40] !== bookingError || $[41] !== bookingPending || $[42] !== hasTimeslots || $[43] !== t11 || $[44] !== t12 || $[45] !== t13 || $[46] !== t15) {
    t16 = <div className="sm:hidden"><AppExperienceBookingBoxMobile basePrice={t11} selectedTimeslot={t12} bookingError={bookingError} bookingPending={bookingPending} onCtaClick={t13} onDateClick={t14} hasTimeslots={hasTimeslots} hasAvailableDates={t15} /></div>;
    $[40] = bookingError;
    $[41] = bookingPending;
    $[42] = hasTimeslots;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const t17 = !isMobile && activeModal === FilterType.variant;
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setActiveModal(null);
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t17) {
    t19 = <AppVariantModal isOpen={t17} onClose={t18} />;
    $[49] = t17;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const t20 = activeModal === FilterType.person;
  const t21 = isMobile && isNavigatingToChoices;
  let t22;
  if ($[51] !== isNavigatingToChoices) {
    t22 = () => {
      if (!isNavigatingToChoices) {
        setActiveModal(null);
      }
    };
    $[51] = isNavigatingToChoices;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== handlePaxSubmit || $[54] !== t20 || $[55] !== t21 || $[56] !== t22) {
    t23 = <AppPersonModal isOpen={t20} isSubmitting={t21} onSubmit={handlePaxSubmit} onClose={t22} />;
    $[53] = handlePaxSubmit;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const t24 = activeModal === FilterType.date;
  let t25;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setActiveModal(null);
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== availableDates || $[60] !== experience.userTimeslotRequestsAllowed || $[61] !== t24) {
    t26 = <AppDateModal isOpen={t24} onClose={t25} availableDates={availableDates} userTimeslotRequestsAllowed={experience.userTimeslotRequestsAllowed} />;
    $[59] = availableDates;
    $[60] = experience.userTimeslotRequestsAllowed;
    $[61] = t24;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  const t27 = isMobile && activeModal === FilterType.variant;
  let t28;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setActiveModal(null);
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t27) {
    t29 = <AppMultiVariantModal isOpen={t27} onClose={t28} />;
    $[64] = t27;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== availableDates || $[67] !== isEdpBookingOpen || $[68] !== isPreliminary || $[69] !== isVariant || $[70] !== setIsEdpBookingOpen) {
    t30 = isVariant && <AppEdpBookingModal isOpen={isEdpBookingOpen} onClose={() => setIsEdpBookingOpen(false)} availableDates={availableDates} availableDatesArePreliminary={isPreliminary} />;
    $[66] = availableDates;
    $[67] = isEdpBookingOpen;
    $[68] = isPreliminary;
    $[69] = isVariant;
    $[70] = setIsEdpBookingOpen;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t10 || $[73] !== t16 || $[74] !== t19 || $[75] !== t23 || $[76] !== t26 || $[77] !== t29 || $[78] !== t30) {
    t31 = <>{t10}{t16}{t19}{t23}{t26}{t29}{t30}</>;
    $[72] = t10;
    $[73] = t16;
    $[74] = t19;
    $[75] = t23;
    $[76] = t26;
    $[77] = t29;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  return t31;
}